import React, { useEffect, useState } from 'react';
import { X, TrendingUp, Download, BarChart3, Table, Calendar } from 'lucide-react';
import { crmAnalytics, crmListSummary, crmCompanyDetail } from '../../../../services/vinculationCRMService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import TooltipHelper from '../../../../components/common/TooltipHelper';

const fmt = (n) => new Intl.NumberFormat('es-CL').format(Number.isFinite(n) ? n : 0);
const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

// Función para formatear fecha de YYYY-MM-DD a DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const HistoricalChartModal = ({ 
  open, 
  onClose, 
  excludeRut,
  year: initialYear,
  month: initialMonth,
  yearFrom: initialYearFrom,
  monthFrom: initialMonthFrom,
  yearTo: initialYearTo,
  monthTo: initialMonthTo,
  maxDataYear
}) => {
  const [loading, setLoading] = useState(true);
  const [historicalData, setHistoricalData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [activeTab, setActiveTab] = useState('yearly'); // 'yearly', 'monthly', 'table' - Inicia en yearly por defecto
  
  // Estados locales para los filtros (solo rango de fechas)
  const [yearFrom, setYearFrom] = useState(initialYearFrom || '');
  const [monthFrom, setMonthFrom] = useState(initialMonthFrom || '');
  const [yearTo, setYearTo] = useState(initialYearTo || '');
  const [monthTo, setMonthTo] = useState(initialMonthTo || '');
  const [yearOptions, setYearOptions] = useState([]);
  
  // Total de reuniones en el periodo seleccionado
  const [totalMeetingsInPeriod, setTotalMeetingsInPeriod] = useState(0);
  
  // Nombres de meses
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  
  // Función para exportar a Excel (datos históricos con filtros de tiempo si están aplicados)
  const handleExportToExcel = async () => {
    // Mostrar confirmación con información
    const confirmResult = await Swal.fire({
      title: '¿Exportar histórico de reuniones?',
      html: `
        <div class="text-left">
          <p class="mb-3">Se exportará el <strong>histórico completo de reuniones</strong> según los filtros aplicados.</p>
          <p class="text-sm text-gray-600">El archivo Excel incluirá todas las reuniones en el período seleccionado.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, exportar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    // Mostrar modal de carga
    const loadingToast = Swal.fire({
      title: 'Exportando histórico de reuniones...',
      html: 'Generando archivo Excel con el histórico completo de reuniones según los filtros aplicados. Por favor espera.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      setLoading(true);
      
      // Determinar rango de años según filtros
      let finalStartYear, endYear, years;
      
      if (yearFrom && yearTo) {
        // Usar filtros de rango
        finalStartYear = Number(yearFrom);
        endYear = Number(yearTo);
        years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
      } else {
        // Sin filtros, usar comportamiento original
        const currentYear = new Date().getFullYear();
        const startYear = maxDataYear ? maxDataYear - 15 : currentYear - 15;
        endYear = maxDataYear || currentYear;
        finalStartYear = endYear - startYear > 20 ? endYear - 20 : startYear;
        years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
      }
      
      // Primero cargar todas las reuniones para calcular socios únicos por año (en paralelo)
      const allMeetingsForUniqueCount = [];
      try {
        const companies = await crmListSummary(undefined);
        const companiesArray = Array.isArray(companies) ? companies : [];
        
        // Procesar empresas en lotes paralelos
        const batchSize = 20;
        for (let i = 0; i < companiesArray.length; i += batchSize) {
          const batch = companiesArray.slice(i, i + batchSize);
          
          const batchResults = await Promise.allSettled(
            batch.map(async (company) => {
              const rut = company.rut || company.rutEmpresa;
              if (!rut) return [];
              
              // Excluir admin/propia empresa si es necesario
              if (excludeRut && String(rut).replace(/\./g, '').replace(/-/g, '') === String(excludeRut).replace(/\./g, '').replace(/-/g, '')) {
                return [];
              }
              
              try {
                const detail = await crmCompanyDetail(rut);
                const meetings = Array.isArray(detail?.meetings) ? detail.meetings : [];
                
                // Agregar todas las reuniones con su año y companyRut
                return meetings
                  .filter(meeting => meeting.date)
                  .map(meeting => {
                    const meetingYear = parseInt(meeting.date.split('-')[0]);
                    if (meetingYear >= finalStartYear && meetingYear <= endYear) {
                      return {
                        year: meetingYear,
                        companyRut: meeting.companyRut || rut
                      };
                    }
                    return null;
                  })
                  .filter(Boolean);
              } catch (e) {
                console.error(`Error loading meetings for company ${rut}:`, e);
                return [];
              }
            })
          );
          
          // Agregar resultados exitosos
          batchResults.forEach((result) => {
            if (result.status === 'fulfilled' && Array.isArray(result.value)) {
              allMeetingsForUniqueCount.push(...result.value);
            }
          });
          
          // Actualizar progreso
          if (loadingToast && loadingToast.isVisible && i + batchSize < companiesArray.length) {
            const progress = Math.round(((i + batchSize) / companiesArray.length) * 100);
            loadingToast.update({
              html: `Exportando histórico de reuniones...<br/>Cargando datos anuales: ${Math.min(i + batchSize, companiesArray.length)} de ${companiesArray.length} (${progress}%)`
            });
          }
        }
      } catch (e) {
        console.error('Error loading meetings for unique count:', e);
      }
      
      // Contar socios únicos por año
      const uniqueCompaniesByYear = {};
      allMeetingsForUniqueCount.forEach(({ year, companyRut }) => {
        if (!companyRut) return;
        if (!uniqueCompaniesByYear[year]) {
          uniqueCompaniesByYear[year] = new Set();
        }
        uniqueCompaniesByYear[year].add(companyRut);
      });
      
      // Cargar todos los años históricos con conteo de socios únicos
      const allHistoricalData = await Promise.all(
        years.map(async (y) => {
          try {
            const analyticsParams = { year: y, excludeRut };
            const data = await crmAnalytics(analyticsParams);
            const count = num(data?.totals?.meetings, 0);
            const uniqueCompanies = uniqueCompaniesByYear[y] ? uniqueCompaniesByYear[y].size : 0;
            
            return { 
              label: String(y),
              period: String(y),
              count: count,
              uniqueCompanies: uniqueCompanies
            };
          } catch (e) {
            console.error(`Error loading year ${y}:`, e);
            return { 
              label: String(y),
              period: String(y),
              count: 0,
              uniqueCompanies: 0
            };
          }
        })
      );
      
      // Cargar datos históricos por meses (respetando filtros de mes si están aplicados)
      const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      
      // Determinar rango de meses según filtros
      let monthStart = 1;
      let monthEnd = 12;
      
      if (yearFrom && monthFrom && yearTo && monthTo) {
        // Si hay filtros de mes, usarlos solo para el primer y último año
        // Para años intermedios, usar todos los meses
        monthStart = Number(monthFrom);
        monthEnd = Number(monthTo);
      }
      
      // Preparar todas las llamadas mensuales en paralelo
      const monthlyPromises = [];
      for (const y of years) {
        // Determinar qué meses cargar para este año
        let startMonth = 1;
        let endMonth = 12;
        
        if (yearFrom && monthFrom && yearTo && monthTo) {
          if (y === finalStartYear) {
            // Primer año: desde monthFrom
            startMonth = Number(monthFrom);
            endMonth = 12;
          } else if (y === endYear) {
            // Último año: hasta monthTo
            startMonth = 1;
            endMonth = Number(monthTo);
          }
          // Años intermedios: todos los meses
        }
        
        for (let m = startMonth; m <= endMonth; m++) {
          // Crear promesa para este mes
          const monthPromise = crmAnalytics({ 
            year: y, 
            month: m,
            excludeRut 
          }).then((data) => {
            const count = num(data?.totals?.meetings, 0);
            return {
              year: String(y),
              month: m,
              monthName: monthNames[m - 1],
              label: `${monthNames[m - 1]} ${y}`,
              period: `${y}-${String(m).padStart(2, '0')}`,
              count: count
            };
          }).catch((e) => {
            console.error(`Error loading ${y}-${m}:`, e);
            return {
              year: String(y),
              month: m,
              monthName: monthNames[m - 1],
              label: `${monthNames[m - 1]} ${y}`,
              period: `${y}-${String(m).padStart(2, '0')}`,
              count: 0
            };
          });
          
          monthlyPromises.push(monthPromise);
        }
      }
      
      // Ejecutar todas las llamadas mensuales en paralelo (en lotes para no sobrecargar)
      const allMonthlyData = [];
      const monthlyBatchSize = 20; // Procesar 20 meses a la vez
      for (let i = 0; i < monthlyPromises.length; i += monthlyBatchSize) {
        const batch = monthlyPromises.slice(i, i + monthlyBatchSize);
        // Actualizar progreso durante procesamiento de meses
        if (loadingToast && loadingToast.isVisible && i + monthlyBatchSize < monthlyPromises.length) {
          const progress = Math.round(((i + monthlyBatchSize) / monthlyPromises.length) * 100);
          loadingToast.update({
            html: `Exportando histórico de reuniones...<br/>Procesando meses: ${Math.min(i + monthlyBatchSize, monthlyPromises.length)} de ${monthlyPromises.length} (${progress}%)`
          });
        }
        const results = await Promise.all(batch);
        allMonthlyData.push(...results);
      }
      
      const workbook = new ExcelJS.Workbook();
      
      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0488AF' }
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF09274A' } },
        alignment: { horizontal: 'left', vertical: 'middle' }
      };
      
      const dataStyle = {
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        }
      };
      
      // Hoja 1: Datos históricos principales (por año)
      const mainSheet = workbook.addWorksheet('Datos Históricos');
      
      // Título con filtros si están aplicados
      let titleText = 'Reuniones por año';
      if (yearFrom && yearTo) {
        if (monthFrom && monthTo) {
          titleText = `Reuniones por año (${monthNames[Number(monthFrom) - 1]} ${yearFrom} - ${monthNames[Number(monthTo) - 1]} ${yearTo})`;
        } else {
          titleText = `Reuniones por año (${yearFrom} - ${yearTo})`;
        }
      } else {
        titleText = 'Reuniones por año (Datos Históricos Completos)';
      }
      
      // Título
      mainSheet.mergeCells('A1:C1');
      mainSheet.getCell('A1').value = titleText;
      mainSheet.getCell('A1').style = titleStyle;
      mainSheet.getRow(1).height = 25;
      
      // Resumen
      const totalMeetings = allHistoricalData.reduce((sum, d) => sum + num(d.count, 0), 0);
      const totalUniqueCompanies = allHistoricalData.reduce((sum, d) => sum + num(d.uniqueCompanies || 0, 0), 0);
      
      mainSheet.mergeCells('A2:C2');
      mainSheet.getCell('A2').value = `Total de reuniones: ${fmt(totalMeetings)}`;
      mainSheet.getRow(2).height = 20;
      
      // Espacio
      mainSheet.getRow(3).height = 10;
      
      // Encabezados
      mainSheet.getCell('A4').value = 'Año';
      mainSheet.getCell('B4').value = 'Cantidad de Reuniones';
      mainSheet.getCell('C4').value = 'Socios Únicos';
      mainSheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      mainSheet.getRow(4).height = 25;
      
      // Datos
      allHistoricalData.forEach(({ label, count, uniqueCompanies }, index) => {
        const row = mainSheet.getRow(index + 5);
        row.getCell(1).value = label;
        row.getCell(2).value = num(count, 0);
        row.getCell(3).value = num(uniqueCompanies || 0, 0);
        row.getCell(2).numFmt = '#,##0';
        row.getCell(3).numFmt = '#,##0';
        
        row.eachCell((cell) => {
          cell.style = dataStyle;
        });
        row.height = 20;
      });
      
      // Ajustar ancho de columnas
      mainSheet.getColumn(1).width = 20;
      mainSheet.getColumn(2).width = 25;
      mainSheet.getColumn(3).width = 20;
      
      // Hoja 2: Histórico desglosado por meses
      const monthlySheet = workbook.addWorksheet('Histórico por Meses');
        
        // Título
      monthlySheet.mergeCells('A1:C1');
      monthlySheet.getCell('A1').value = 'Reuniones por mes (Datos Históricos Completos)';
      monthlySheet.getCell('A1').style = titleStyle;
      monthlySheet.getRow(1).height = 25;
        
        // Resumen
      const totalMonthlyMeetings = allMonthlyData.reduce((sum, d) => sum + num(d.count, 0), 0);
      
      monthlySheet.mergeCells('A2:C2');
      monthlySheet.getCell('A2').value = `Total de reuniones: ${fmt(totalMonthlyMeetings)}`;
      monthlySheet.getRow(2).height = 20;
        
        // Espacio
      monthlySheet.getRow(3).height = 10;
        
        // Encabezados
      monthlySheet.getCell('A4').value = 'Año';
      monthlySheet.getCell('B4').value = 'Mes';
      monthlySheet.getCell('C4').value = 'Cantidad de Reuniones';
      monthlySheet.getRow(4).eachCell((cell) => {
        cell.style = headerStyle;
      });
      monthlySheet.getRow(4).height = 25;
      
      // Datos
      allMonthlyData.forEach(({ year, monthName, count }, index) => {
        const row = monthlySheet.getRow(index + 5);
        row.getCell(1).value = year;
        row.getCell(2).value = monthName;
        row.getCell(3).value = num(count, 0);
        row.getCell(3).numFmt = '#,##0';
        
          row.eachCell((cell) => {
              cell.style = dataStyle;
          });
          row.height = 20;
        });
        
        // Ajustar ancho de columnas
      monthlySheet.getColumn(1).width = 15;
      monthlySheet.getColumn(2).width = 20;
      monthlySheet.getColumn(3).width = 25;
      
      // Hoja 3: Detalle de reuniones con información de socios y tipo
      const detailSheet = workbook.addWorksheet('Detalle de Reuniones');
      
      // Título (siempre completo, sin filtros)
      detailSheet.mergeCells('A1:F1');
      detailSheet.getCell('A1').value = 'Detalle de reuniones con información de socios y tipo (Datos Históricos Completos)';
      detailSheet.getCell('A1').style = titleStyle;
      detailSheet.getRow(1).height = 25;
      
      // Espacio
      detailSheet.getRow(2).height = 10;
      
      // Encabezados
      detailSheet.getCell('A3').value = 'Año';
      detailSheet.getCell('B3').value = 'Mes';
      detailSheet.getCell('C3').value = 'Empresa';
      detailSheet.getCell('D3').value = 'Fecha';
      detailSheet.getCell('E3').value = 'Número de participantes';
      detailSheet.getCell('F3').value = 'Tipo de reunión';
      detailSheet.getRow(3).eachCell((cell) => {
        cell.style = headerStyle;
      });
      detailSheet.getRow(3).height = 25;
      
      // Cargar todas las empresas y sus reuniones (SIEMPRE completo, sin filtros) - en paralelo
      try {
        const companies = await crmListSummary(undefined);
        const allMeetings = [];
        
        // Para la hoja de detalle, siempre usar el rango completo histórico (últimos 15-20 años)
        const currentYear = new Date().getFullYear();
        const startYear = maxDataYear ? maxDataYear - 15 : currentYear - 15;
        const endYearDetail = maxDataYear || currentYear;
        const finalStartYearDetail = endYearDetail - startYear > 20 ? endYearDetail - 20 : startYear;
        
        const companiesArray = Array.isArray(companies) ? companies : [];
        
        // Función auxiliar para procesar una empresa
        const processCompany = async (company) => {
          const rut = company.rut || company.rutEmpresa;
          if (!rut) return [];
          
          // Excluir admin/propia empresa si es necesario
          if (excludeRut && String(rut).replace(/\./g, '').replace(/-/g, '') === String(excludeRut).replace(/\./g, '').replace(/-/g, '')) {
            return [];
          }
          
          try {
            const detail = await crmCompanyDetail(rut);
            const meetings = Array.isArray(detail?.meetings) ? detail.meetings : [];
            
            // Filtrar reuniones solo por el rango histórico completo (sin filtros del usuario)
            const filteredMeetings = meetings.filter((m) => {
              if (!m.date) return false;
              const meetingYear = parseInt(m.date.split('-')[0]);
              return meetingYear >= finalStartYearDetail && meetingYear <= endYearDetail;
            });
            
            // Nombre de la empresa
            const companyName = company.trade_name || company.business_name || rut || '—';
            
            // Procesar cada reunión
            return filteredMeetings
              .filter(meeting => meeting.date)
              .map((meeting) => {
                const dateParts = meeting.date.split('-');
                if (dateParts.length !== 3) return null;
                
                const meetingYear = parseInt(dateParts[0]);
                const meetingMonth = parseInt(dateParts[1]);
                const meetingDate = meeting.date;
                
                // Contar participantes
                const participants = Array.isArray(meeting.participants) ? meeting.participants : [];
                const participantsCount = participants.length;
                
                // Obtener tipo de reunión (tags o category)
                const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
                const category = meeting.category || '';
                const meetingType = tags.length > 0 ? tags.join(', ') : (category || '—');
                
                return {
                  year: meetingYear,
                  month: meetingMonth,
                  monthName: monthNames[meetingMonth - 1] || '',
                  companyName: companyName,
                  date: meetingDate,
                  participantsCount: participantsCount,
                  meetingType: meetingType,
                };
              })
              .filter(Boolean);
          } catch (e) {
            console.error(`Error loading meetings for company ${rut}:`, e);
            return [];
          }
        };
        
        // Procesar empresas en lotes paralelos
        const batchSize = 20;
        for (let i = 0; i < companiesArray.length; i += batchSize) {
          const batch = companiesArray.slice(i, i + batchSize);
          const results = await Promise.allSettled(batch.map(processCompany));
          
          results.forEach((result) => {
            if (result.status === 'fulfilled' && Array.isArray(result.value)) {
              allMeetings.push(...result.value);
            }
          });
          
          // Actualizar progreso
          if (loadingToast && loadingToast.isVisible && i + batchSize < companiesArray.length) {
            const progress = Math.round(((i + batchSize) / companiesArray.length) * 100);
            loadingToast.update({
              html: `Exportando histórico de reuniones...<br/>Procesando empresas para detalle de reuniones: ${Math.min(i + batchSize, companiesArray.length)} de ${companiesArray.length} (${progress}%)`
            });
          }
        }
        
        // Ordenar por año, mes y fecha
        allMeetings.sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          if (a.month !== b.month) return a.month - b.month;
          return a.date.localeCompare(b.date);
        });
        
        // Agregar datos a la hoja
        allMeetings.forEach((meeting, index) => {
          const row = detailSheet.getRow(index + 4);
          row.getCell(1).value = meeting.year;
          row.getCell(2).value = meeting.monthName;
          row.getCell(3).value = meeting.companyName;
          row.getCell(4).value = formatDate(meeting.date);
          row.getCell(5).value = meeting.participantsCount;
          row.getCell(6).value = meeting.meetingType;
          
          row.getCell(1).numFmt = '#,##0';
          row.getCell(5).numFmt = '#,##0';
          
          row.eachCell((cell) => {
            cell.style = {
              ...dataStyle,
              alignment: { horizontal: cell.col === 3 || cell.col === 6 ? 'left' : 'center', vertical: 'middle' },
            };
          });
          row.height = 20;
        });
        
        // Ajustar ancho de columnas
        detailSheet.getColumn(1).width = 12;
        detailSheet.getColumn(2).width = 15;
        detailSheet.getColumn(3).width = 35;
        detailSheet.getColumn(4).width = 15;
        detailSheet.getColumn(5).width = 18;
        detailSheet.getColumn(6).width = 30;
      } catch (error) {
        console.error('Error loading meeting details:', error);
        detailSheet.getCell('A4').value = 'Error al cargar los detalles de las reuniones';
        detailSheet.getCell('A4').style = { font: { italic: true, color: { argb: 'FFFF0000' } } };
      }
      
      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Nombre del archivo
      const fileName = `Historico_Reuniones_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
      
      // Cerrar modal de carga y mostrar éxito
      await Swal.fire({
        icon: 'success',
        title: 'Excel generado',
        text: 'El archivo Excel se ha generado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      // Cerrar modal de carga y mostrar error
      await Swal.fire({
        icon: 'error',
        title: 'Error al exportar',
        text: 'No se pudo exportar los datos a Excel. Por favor, intenta nuevamente.',
        confirmButtonText: 'Aceptar',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Inicializar yearOptions cuando se abre el modal
  useEffect(() => {
    if (open && yearOptions.length === 0) {
      const loadYearOptions = async () => {
        try {
          const s0 = await crmListSummary(undefined);
          const currentYear = new Date().getFullYear();
          const years = Array.isArray(s0) && s0.length > 0
            ? Array.from({ length: 7 }, (_, i) => (maxDataYear || currentYear) - i)
            : Array.from({ length: 7 }, (_, i) => currentYear - i);
          setYearOptions(['', ...years]);
        } catch (e) {
          const currentYear = new Date().getFullYear();
          setYearOptions(['', ...Array.from({ length: 7 }, (_, i) => currentYear - i)]);
        }
      };
      loadYearOptions();
    }
  }, [open, maxDataYear]);
  

  // Resetear filtros y pestañas cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setYearFrom(initialYearFrom || '');
      setMonthFrom(initialMonthFrom || '');
      setYearTo(initialYearTo || '');
      setMonthTo(initialMonthTo || '');
      setActiveTab('yearly'); // Resetear a yearly por defecto
      setTotalMeetingsInPeriod(0);
      setYearlyData([]);
    }
  }, [open, initialYearFrom, initialMonthFrom, initialYearTo, initialMonthTo]);


  // Cargar datos cuando cambian los filtros o se abre el modal
  useEffect(() => {
    if (!open) return;

    const loadHistoricalData = async () => {
      setLoading(true);
      try {
        // Asegurar que yearOptions esté inicializado
        if (yearOptions.length === 0) {
          const currentYear = new Date().getFullYear();
          const defaultYears = Array.from({ length: 7 }, (_, i) => (maxDataYear || currentYear) - i);
          setYearOptions(['', ...defaultYears]);
        }

        let results = [];
        let yearlyResults = [];

        // Cargar datos anuales si estamos en la pestaña de histórico anual
        if (activeTab === 'yearly') {
          const currentYear = new Date().getFullYear();
          let startYear = maxDataYear ? maxDataYear - 15 : currentYear - 15;
          let endYear = maxDataYear || currentYear;
          
          // Si hay filtros de rango de fechas, usar solo esos años
          if (yearFrom && yearTo) {
            startYear = Number(yearFrom);
            endYear = Number(yearTo);
          }
          
          // Asegurar que el rango no sea demasiado grande
          const finalStartYear = endYear - startYear > 20 ? endYear - 20 : startYear;
          const years = Array.from({ length: endYear - finalStartYear + 1 }, (_, i) => finalStartYear + i);
          
          // Cargar datos por año
          for (const y of years) {
            try {
              const analyticsParams = { year: y, excludeRut };
              const data = await crmAnalytics(analyticsParams);
              const count = num(data?.totals?.meetings, 0);
              yearlyResults.push({
                label: String(y),
                period: String(y),
                count: count
              });
            } catch (e) {
              console.error(`Error loading year ${y}:`, e);
              yearlyResults.push({
                label: String(y),
                period: String(y),
                count: 0
              });
            }
          }
          
          // Ordenar por año de forma ascendente (más antiguo primero)
          yearlyResults.sort((a, b) => Number(a.label) - Number(b.label));
          
          const totalYearly = yearlyResults.reduce((sum, d) => sum + num(d.count, 0), 0);
          setTotalMeetingsInPeriod(totalYearly);
          setYearlyData(yearlyResults);
          setHistoricalData([]);
        }
        // Cargar datos mensuales si estamos en la pestaña de histórico mensual y hay rango de fechas
        else if (activeTab === 'monthly' || activeTab === 'table') {
          // Permitir filtrar solo por años (sin meses)
          if (yearFrom && yearTo) {
            const startYearNum = Number(yearFrom);
            const endYearNum = Number(yearTo);
            
            // Si no hay mes desde, usar enero (1)
            // Si no hay mes hasta, usar diciembre (12)
            const startMonthNum = monthFrom ? Number(monthFrom) : 1;
            const endMonthNum = monthTo ? Number(monthTo) : 12;
            
            const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                              'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
            
            // Generar todos los meses en el rango
            const monthData = [];
            let currentYear = startYearNum;
            let currentMonth = startMonthNum;
            let totalCount = 0;
            
            while (currentYear < endYearNum || (currentYear === endYearNum && currentMonth <= endMonthNum)) {
              try {
                const analyticsParams = { 
                  year: currentYear, 
                  month: currentMonth,
                  excludeRut 
                };
                const data = await crmAnalytics(analyticsParams);
                const count = num(data?.totals?.meetings, 0);
                totalCount += count;
                monthData.push({ 
                  label: `${monthNames[currentMonth - 1]} ${currentYear}`,
                  period: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
                  count: count 
                });
              } catch (e) {
                console.error(`Error loading ${currentYear}-${currentMonth}:`, e);
                monthData.push({ 
                  label: `${monthNames[currentMonth - 1]} ${currentYear}`,
                  period: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
                  count: 0 
                });
              }
              
              // Avanzar al siguiente mes
              currentMonth++;
              if (currentMonth > 12) {
                currentMonth = 1;
                currentYear++;
              }
            }
            
            setTotalMeetingsInPeriod(totalCount);
            results = monthData;
            setHistoricalData(results.filter(r => r !== null));
            setYearlyData([]);
          } else {
            // Si no hay años seleccionados, no cargar datos
            results = [];
            setTotalMeetingsInPeriod(0);
            setHistoricalData([]);
          }
        }
      } catch (e) {
        console.error('Error loading historical data:', e);
        setHistoricalData([]);
        setYearlyData([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistoricalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, excludeRut, yearFrom, monthFrom, yearTo, monthTo, maxDataYear, activeTab]);

  // Determinar qué datos mostrar según la pestaña activa
  const dataToShow = activeTab === 'yearly' ? yearlyData : historicalData;
  const chartTitle = activeTab === 'yearly' ? 'Reuniones por año' : 'Reuniones por mes';

  if (!open) return null;

  const max = Math.max(...dataToShow.map(s => num(s.count, 0)), 1);
  const totalItems = dataToShow.length;
  
  // Determinar columnas dinámicamente para el gráfico principal
  let gridCols = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
  if (totalItems > 10) {
    gridCols = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10';
  } else if (totalItems > 5) {
    gridCols = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6';
  }
  

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative min-h-full flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-[95vw] xl:max-w-[90rem] bg-white rounded-2xl shadow-xl border border-gray-200 my-4 max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5" />
              <h2 className="text-xl font-bold">Histórico de reuniones</h2>
              <TooltipHelper text="El histórico mensual y los datos tabulares estarán disponibles al seleccionar un rango de fechas (puedes filtrar solo por año o por mes y año)" />
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportToExcel}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  title="Exportar a Excel"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">Exportar Excel</span>
                </button>
                <TooltipHelper text="Exporta los datos históricos de reuniones a un archivo Excel con múltiples hojas: datos por año, datos por mes y detalle completo de reuniones." />
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                title="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="px-6 py-4 bg-white border-b border-gray-200">
            <div className="space-y-4">
              {/* Solo modo rango de fechas */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-[#0488AF]" />
                  <label className="text-sm font-semibold text-gray-800">Rango de fechas</label>
                </div>

                {/* Campos de entrada de fechas */}
                <div className="flex items-center gap-3">
                  {/* Desde */}
                  <div className="flex-1 space-y-2">
                    <label className="text-xs text-gray-600">Desde</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        <select
                          value={yearFrom || ''}
                          onChange={(e) => setYearFrom(e.target.value)}
                          className="w-full h-10 pl-9 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                        >
                          <option value="">Año</option>
                          {yearOptions.filter(y => y).map((y, i) => (
                            <option key={i} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {(yearFrom || monthFrom) && (
                        <button
                          onClick={() => {
                            setYearFrom('');
                            setMonthFrom('');
                          }}
                          className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                          title="Limpiar filtro Desde"
                        >
                          Limpiar
                        </button>
                      )}
                      {yearFrom && (
                        <div className="relative flex-1">
                          <select
                            value={monthFrom || ''}
                            onChange={(e) => setMonthFrom(e.target.value)}
                            className="w-full h-10 pl-3 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                          >
                            <option value="">Mes</option>
                            {[
                              { value: '1', label: 'Enero' },
                              { value: '2', label: 'Febrero' },
                              { value: '3', label: 'Marzo' },
                              { value: '4', label: 'Abril' },
                              { value: '5', label: 'Mayo' },
                              { value: '6', label: 'Junio' },
                              { value: '7', label: 'Julio' },
                              { value: '8', label: 'Agosto' },
                              { value: '9', label: 'Septiembre' },
                              { value: '10', label: 'Octubre' },
                              { value: '11', label: 'Noviembre' },
                              { value: '12', label: 'Diciembre' },
                            ].map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Separador */}
                  <div className="pt-6">
                    <span className="text-gray-400 font-medium">-</span>
                  </div>

                  {/* Hasta */}
                  <div className="flex-1 space-y-2">
                    <label className="text-xs text-gray-600">Hasta</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        <select
                          value={yearTo || ''}
                          onChange={(e) => setYearTo(e.target.value)}
                          className="w-full h-10 pl-9 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                        >
                          <option value="">Año</option>
                          {yearOptions.filter(y => y).map((y, i) => (
                            <option key={i} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {(yearTo || monthTo) && (
                        <button
                          onClick={() => {
                            setYearTo('');
                            setMonthTo('');
                          }}
                          className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                          title="Limpiar filtro Hasta"
                        >
                          Limpiar
                        </button>
                      )}
                      {yearTo && (
                        <div className="relative flex-1">
                          <select
                            value={monthTo || ''}
                            onChange={(e) => setMonthTo(e.target.value)}
                            className="w-full h-10 pl-3 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                          >
                            <option value="">Mes</option>
                            {[
                              { value: '1', label: 'Enero' },
                              { value: '2', label: 'Febrero' },
                              { value: '3', label: 'Marzo' },
                              { value: '4', label: 'Abril' },
                              { value: '5', label: 'Mayo' },
                              { value: '6', label: 'Junio' },
                              { value: '7', label: 'Julio' },
                              { value: '8', label: 'Agosto' },
                              { value: '9', label: 'Septiembre' },
                              { value: '10', label: 'Octubre' },
                              { value: '11', label: 'Noviembre' },
                              { value: '12', label: 'Diciembre' },
                            ].map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 flex-1 overflow-y-auto">
            {/* Contenido principal - Pestañas y gráficos */}
            <div className="w-full">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Cargando datos históricos...</div>
                  </div>
                ) : (
                  <>
                    {/* Pestañas - Siempre visibles */}
                    <div className="mb-4 border-b border-gray-200">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setActiveTab('yearly')}
                          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                            activeTab === 'yearly'
                              ? 'bg-[#0488AF] text-white border-b-2 border-[#0488AF]'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          <BarChart3 className="w-4 h-4" />
                          Histórico anual
                        </button>
                        <button
                          onClick={() => setActiveTab('monthly')}
                          disabled={!yearFrom || !yearTo}
                          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                            activeTab === 'monthly'
                              ? 'bg-[#0488AF] text-white border-b-2 border-[#0488AF]'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          } ${(!yearFrom || !yearTo) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <BarChart3 className="w-4 h-4" />
                          Histórico mensual
                        </button>
                        <button
                          onClick={() => setActiveTab('table')}
                          disabled={!yearFrom || !yearTo}
                          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                            activeTab === 'table'
                              ? 'bg-[#0488AF] text-white border-b-2 border-[#0488AF]'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          } ${(!yearFrom || !yearTo) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Table className="w-4 h-4" />
                          Datos Tabulares
                        </button>
                      </div>
                    </div>

                    {/* Contenido según la pestaña activa */}
                    {activeTab === 'yearly' ? (
                      <>
                        {loading ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="text-gray-500">Cargando datos históricos anuales...</div>
                          </div>
                        ) : yearlyData.length === 0 ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="text-gray-500">No hay datos históricos anuales disponibles</div>
                          </div>
                        ) : (
                          <>
                            {/* Resumen - Mostrar total de reuniones anuales */}
                            <div className="mb-4 md:mb-6 p-4 md:p-6 bg-gradient-to-r from-[#0488AF] to-[#09274A] rounded-xl text-white shadow-lg">
                              <div className="text-sm md:text-base font-medium mb-2 opacity-90">
                                {yearFrom && yearTo 
                                  ? `Total de reuniones (histórico anual filtrado: ${yearFrom} - ${yearTo})`
                                  : 'Total de reuniones (histórico anual completo)'}
                              </div>
                              <div className="text-3xl md:text-4xl font-bold">
                                {fmt(totalMeetingsInPeriod)}
                              </div>
                              {yearlyData.length > 0 && (
                                <div className="text-xs md:text-sm mt-2 opacity-75">
                                  Desde {yearlyData[0]?.label} hasta {yearlyData[yearlyData.length - 1]?.label}
                                </div>
                              )}
                            </div>

                            {/* Contenido de las pestañas */}
                            <div className="bg-white border rounded-xl p-4 md:p-6 shadow-sm">
                              <div>
                                <div className="mb-4 flex items-center justify-between">
                                  <div className="font-semibold text-[#09274A] text-base">
                                    {chartTitle}
                                  </div>
                                </div>
                                {/* Gráfico completo */}
                                <div className="overflow-x-auto">
                                  <div className={`grid ${gridCols} gap-2 md:gap-4 min-w-max`}>
                                    {yearlyData.map(({ label, period, count }) => {
                                      const h = Math.round((num(count, 0) / max) * 100);
                                      return (
                                        <div key={period} className="flex flex-col items-center justify-end min-h-[200px] md:min-h-[250px]">
                                          <div className="w-full h-full flex items-end" title={`${label}: ${fmt(count)} reuniones`}>
                                            <div 
                                              className="w-full bg-gradient-to-t from-[#0488AF] to-[#0488AF]/70 rounded-t transition-all hover:from-[#037a98] hover:to-[#037a98]/70"
                                              style={{ height: `${Math.max(h, 5)}%` }}
                                            />
                                          </div>
                                          <div className="mt-2 md:mt-3 text-xs md:text-sm text-gray-700 font-semibold text-center truncate w-full">{label}</div>
                                          <div className="text-[10px] md:text-xs text-gray-500 mt-1">{fmt(count)}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (activeTab === 'monthly' || activeTab === 'table') && (!yearFrom || !yearTo) ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <div className="text-gray-500 mb-2">Selecciona un rango de años para ver el histórico mensual</div>
                          <div className="text-sm text-gray-400">Puedes seleccionar solo años o años con meses específicos</div>
                        </div>
                      </div>
                    ) : dataToShow.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">No hay datos históricos disponibles para el periodo seleccionado</div>
                      </div>
                    ) : (
                      <>
                        {/* Resumen - Mostrar total de reuniones en el periodo */}
                        {yearFrom && yearTo && (
                          <div className="mb-4 md:mb-6 p-4 md:p-6 bg-gradient-to-r from-[#0488AF] to-[#09274A] rounded-xl text-white shadow-lg">
                            <div className="text-sm md:text-base font-medium mb-2 opacity-90">
                              Total de reuniones en el periodo seleccionado
                            </div>
                            <div className="text-3xl md:text-4xl font-bold">
                              {fmt(totalMeetingsInPeriod)}
                            </div>
                            <div className="text-xs md:text-sm mt-2 opacity-75">
                              {monthFrom && monthTo 
                                ? `${monthNames[Number(monthFrom) - 1]} ${yearFrom} - ${monthNames[Number(monthTo) - 1]} ${yearTo}`
                                : monthFrom 
                                  ? `${monthNames[Number(monthFrom) - 1]} ${yearFrom} - Diciembre ${yearTo}`
                                  : monthTo
                                    ? `Enero ${yearFrom} - ${monthNames[Number(monthTo) - 1]} ${yearTo}`
                                    : `${yearFrom} - ${yearTo} (todos los meses)`}
                            </div>
                          </div>
                        )}

                        {/* Contenido de las pestañas */}
                    {(activeTab === 'monthly' || activeTab === 'table') && (
                      <div className="bg-white border rounded-xl p-4 md:p-6 shadow-sm">
                      {activeTab === 'monthly' && (
                        <div>
                          <div className="mb-4 flex items-center justify-between">
                            <div className="font-semibold text-[#09274A] text-base">
                              {chartTitle}
                            </div>
                          </div>
                          {/* Gráfico completo */}
                          <div className="overflow-x-auto">
                            <div className={`grid ${gridCols} gap-2 md:gap-4 min-w-max`}>
                              {dataToShow.map(({ label, period, count }) => {
                                const h = Math.round((num(count, 0) / max) * 100);
                                return (
                                  <div key={period} className="flex flex-col items-center justify-end min-h-[200px] md:min-h-[250px]">
                                    <div className="w-full h-full flex items-end" title={`${label}: ${fmt(count)} reuniones`}>
                                      <div 
                                        className="w-full bg-gradient-to-t from-[#0488AF] to-[#0488AF]/70 rounded-t transition-all hover:from-[#037a98] hover:to-[#037a98]/70"
                                        style={{ height: `${Math.max(h, 5)}%` }}
                                      />
                                    </div>
                                    <div className="mt-2 md:mt-3 text-xs md:text-sm text-gray-700 font-semibold text-center truncate w-full">{label}</div>
                                    <div className="text-[10px] md:text-xs text-gray-500 mt-1">{fmt(count)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'table' && (
                        <div>
                          <div className="mb-4 font-semibold text-[#09274A] text-base">
                            Datos Tabulares
                          </div>
                          <div className="max-h-96 overflow-y-auto">
                            <div className="divide-y">
                              {dataToShow.map(({ label, period, count }) => (
                                <div key={period} className="flex items-center justify-between py-3 hover:bg-gray-50 px-2 rounded transition-colors">
                                  <div className="font-medium text-sm md:text-base text-gray-800">{label}</div>
                                  <div className="text-sm font-semibold text-[#0488AF]">{fmt(count)} reuniones</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                  </>
                )}
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalChartModal;

